import React from "react";
import BusinessIcon from "@mui/icons-material/Business";
import Modal from "../../Components/Modal";
import {
  EnrichedLoyaltyTier,
  LoyaltyInfo,
} from "../../contexts/CustomerContext";
import LoyaltyHeader from "./LoyaltyHeader";
import TierRow from "./TierRow";
import { formatLoyaltyDate } from "../../utils/format";
import { useLoyaltyBusiness } from "../../hooks/useLoyaltyBusiness";
import { sharedReachText } from "../../utils/loyaltyBusiness";
import styles from "./LoyaltyModal.module.scss";

interface LoyaltyModalProps {
  active: boolean;
  locationId: string;
  locationName: string;
  loyalty: LoyaltyInfo;
  currency: string;
  onClose: () => void;
}

const LoyaltyModal: React.FC<LoyaltyModalProps> = ({
  active,
  locationId,
  locationName,
  loyalty,
  currency,
  onClose,
}) => {
  const { spend, tier, expiresAt, settings } = loyalty;
  const business = useLoyaltyBusiness(loyalty);

  const rawTiers: EnrichedLoyaltyTier[] = [...(settings?.tiers ?? [])].sort(
    (a, b) => a.threshold - b.threshold,
  );
  const displayTiers = rawTiers;
  // An empty body would leave a padded grey strip under the header.
  const hasBody = !!business || displayTiers.length > 0 || !!expiresAt;

  return (
    <Modal
      active={active}
      setActive={onClose}
      style={{
        width: "92%",
        maxWidth: 400,
        maxHeight: "90dvh",
        padding: 0,
        overflow: "hidden",
        borderRadius: 24,
        minHeight: 0,
      }}
    >
      <LoyaltyHeader
        locationId={locationId}
        locationName={locationName}
        business={business}
        loyalty={loyalty}
        currency={currency}
        onClose={onClose}
      />

      {/* ── Body ── */}
      {hasBody && (
        <div className={styles.Body}>
          {business && (
            <div className={styles.SharedNote}>
              <BusinessIcon className={styles.SharedNoteIcon} />
              <p className={styles.SharedNoteText}>
                {sharedReachText(business)}
              </p>
            </div>
          )}

          {displayTiers.length > 0 && (
            <div>
              <p className={styles.SectionTitle}>Tier journey</p>
              <div className={styles.TierList}>
                {displayTiers.map((t, i) => {
                  const isCurrent =
                    !!tier &&
                    t.name === tier.name &&
                    t.threshold === tier.threshold;
                  const currentAscIdx = rawTiers.findIndex(
                    (x) =>
                      tier &&
                      x.name === tier.name &&
                      x.threshold === tier.threshold,
                  );
                  const aboveCurrent =
                    currentAscIdx >= 0 && currentAscIdx + 1 < rawTiers.length
                      ? rawTiers[currentAscIdx + 1].threshold
                      : null;

                  return (
                    <TierRow
                      key={t.id ?? t.name}
                      tier={t}
                      isCurrent={isCurrent}
                      isLast={i === displayTiers.length - 1}
                      spend={spend}
                      nextThreshold={isCurrent ? aboveCurrent : null}
                      pointsRate={settings?.pointsRate ?? null}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {expiresAt && (
            <div className={styles.ExpiryBanner}>
              <span className={styles.ExpiryIcon}>⏳</span>
              <div>
                <p className={styles.ExpiryTitle}>Points expiring soon</p>
                <p className={styles.ExpiryBody}>
                  Some points expire on{" "}
                  <strong>{formatLoyaltyDate(expiresAt)}</strong>. Use them
                  before they're gone!
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default LoyaltyModal;
